import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { keyframes } from '@mui/system';
import { styled, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Link as RouterLink } from 'react-router-dom';

import { FormStepperProvider, useFormStepper } from './FormStepperContext';
import FormStep from './FormStep';

// Shake animation for error fields
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

// Global styles for form animations
const GlobalStyles = styled('style')({
  '@global': {
    '.shake-error': {
      animation: `${shakeAnimation} 0.6s ease-in-out`
    }
  }
});

// Styled components for enhanced visual experience
const StyledPaper = styled(Paper)(({ theme, magical }) => {
  const colors = theme.palette;
  return {
    padding: theme.spacing(3),
    // marginBottom: theme.spacing(3),
    borderRadius: magical ? 16 : 8,
    boxShadow: magical
      ? theme.palette.mode === 'dark'
        ? '0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(123, 104, 238, 0.12)'
        : `0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.04), 0 1px 3px ${alpha(colors.primary.main, 0.12)}`
      : theme.shadows[1],
    overflow: 'hidden',
  };
});

const StepperContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StepContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0, 3),
  minHeight: 200,
  position: 'relative',
}));

const StepNavigationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(4),
}));

// Custom StepIcon component to match the original design
const CustomStepIcon = ({ icon, active, completed, error, clickable }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: error
          ? theme.palette.error.main
          : completed
            ? theme.palette.success.main
            : active
              ? isDark ? theme.palette.secondary.main : theme.palette.primary.main
              : isDark ? alpha(theme.palette.text.disabled, 0.2) : '#E0E0E0',
        color: completed || active || error
          ? theme.palette.common.white
          : isDark ? theme.palette.text.disabled : theme.palette.text.primary,
        transition: 'all 0.3s ease',
        border: !completed && !active && !error ? `1px solid ${isDark ? 'transparent' : '#BDBDBD'}` : 'none',
        cursor: clickable ? 'pointer' : 'default',
        ...(error && {
          boxShadow: `0 0 0 4px ${alpha(theme.palette.error.main, 0.2)}`,
        }),
        ...(active && {
          boxShadow: `0 0 0 4px ${alpha(isDark ? theme.palette.secondary.main : theme.palette.primary.main, 0.15)}`,
        }),
        ...(clickable && !active && {
          '&:hover': {
            transform: 'scale(1.1)',
            boxShadow: `0 0 0 6px ${alpha(
              completed ? theme.palette.success.main : theme.palette.primary.main, 
              0.15
            )}`,
          }
        }),
        '& > *': { // Style for the icon inside
          color: active || completed || error ? theme.palette.common.white : 'inherit',
          fontSize: '1rem',
        },
      }}
    >
      {error ? '!' : (
        icon && React.isValidElement(icon) 
          ? React.cloneElement(icon, { 
              style: { color: active || completed || error ? theme.palette.common.white : 'inherit' } 
            })
          : icon
      )}
    </Box>
  );
};

// Stepper Progress Bar Component
const StepperProgressBar = () => {
  const theme = useTheme();
  const { activeStep, totalSteps } = useFormStepper();
  
  return (
    <Box className="form-stepper-progress" sx={{
      width: '100%',
      height: 4,
      backgroundColor: (theme) => alpha(theme.palette.divider, 0.2),
      borderRadius: 2,
      mb: 3,
      overflow: 'hidden',
      position: 'relative'
    }}>
      <Box sx={{
        height: '100%',
        width: `${(activeStep / (totalSteps - 1)) * 100}%`,
        backgroundImage: (theme) => theme.palette.mode === 'dark'
          ? `linear-gradient(90deg,
              ${alpha(theme.palette.primary.main, 0.8)} 0%,
              ${alpha(theme.palette.secondary.main, 0.9)} 100%)`
          : `linear-gradient(90deg,
              ${theme.palette.primary.main} 0%,
              ${theme.palette.secondary.main} 100%)`,
        borderRadius: 2,
        transition: 'width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
      }} />
    </Box>
  );
};

// Step Navigation Buttons Component
const StepNavigation = ({ 
  cancelLink, 
  backButtonText, 
  nextButtonText,
  submitButtonText,
  submitButtonIcon,
  magical
}) => {
  const theme = useTheme();
  const { 
    activeStep, 
    prevStep, 
    nextStep, 
    isLastStep, 
    submitting,
  } = useFormStepper();
  
  return (
    <StepNavigationContainer>
      {activeStep === 0 && cancelLink ? (
        <Button
          component={RouterLink}
          to={cancelLink}
          startIcon={<ChevronLeftIcon sx={{ fontSize: '1.3rem' }} />}
          variant="outlined"
          sx={{
            color: theme.palette.mode === 'dark' ? undefined : 'primary.main',
            borderColor: theme.palette.mode === 'dark' ? undefined : 'primary.main',
            '& .MuiSvgIcon-root': {
              color: theme.palette.mode === 'dark' ? 'inherit' : theme.palette.primary.main,
              opacity: 1,
              fontSize: '1.3rem'
            }
          }}
        >
          {backButtonText}
        </Button>
      ) : (
        <Button
          type="button"
          disabled={activeStep === 0 || submitting}
          onClick={prevStep}
          startIcon={<ChevronLeftIcon sx={{ fontSize: '1.3rem' }} />}
          variant="outlined"
          sx={{
            color: theme.palette.mode === 'dark' ? undefined : 'primary.main',
            borderColor: theme.palette.mode === 'dark' ? undefined : 'primary.main',
            '& .MuiSvgIcon-root': {
              color: theme.palette.mode === 'dark' ? 'inherit' : theme.palette.primary.main,
              opacity: 1,
              fontSize: '1.3rem'
            }
          }}
        >
          {backButtonText}
        </Button>
      )}
      
      <Box>
        {isLastStep ? (
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              nextStep();
            }}
            endIcon={submitting ?
              <CircularProgress size={20} color="inherit" /> :
              submitButtonIcon ? React.cloneElement(submitButtonIcon, { sx: { color: '#fff', fontSize: '1.3rem' } }) : null
            }
            disabled={submitting}
            sx={magical ? {
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 10px rgba(123, 104, 238, 0.25)'
                : `0 4px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
              background: theme.palette.primary.main,
              '&:hover': {
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 6px 16px rgba(123, 104, 238, 0.4)'
                  : `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                background: theme.palette.primary.dark,
              }
            } : {}}
          >
            {submitButtonText}
          </Button>
        ) : (
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={async (e) => {
              console.log('Next button clicked on step:', activeStep);
              e.preventDefault();
              e.stopPropagation();
              
              // Prevent any form submission
              if (e.target.form) {
                console.log('Button is inside a form, preventing form submission');
                e.target.form.onsubmit = (formEvent) => {
                  formEvent.preventDefault();
                  return false;
                };
              }
              
              try {
                await nextStep();
              } catch (error) {
                console.error('Error in nextStep:', error);
              }
            }}
            endIcon={<ChevronRightIcon sx={{ color: '#fff', fontSize: '1.3rem' }} />}
            disabled={submitting}
            sx={{
              color: '#fff',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 10px rgba(123, 104, 238, 0.25)'
                : `0 4px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 6px 16px rgba(123, 104, 238, 0.4)'
                  : `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
              }
            }}
          >
            {nextButtonText}
          </Button>
        )}
      </Box>
    </StepNavigationContainer>
  );
};

// Completion Step Content Component
const CompletionStepContent = ({ completionContent, onReset }) => {
  return (
    <Box>
      {completionContent || (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            All steps completed!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You have successfully completed all the required steps.
          </Typography>
          <Button onClick={onReset} variant="outlined" sx={{ mt: 2 }}>
            Reset
          </Button>
        </Box>
      )}
    </Box>
  );
};

// StepperVisualization Component
const StepperVisualization = ({ linear }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down('sm') || '(max-width: 600px)');
  const { 
    steps, 
    activeStep, 
    completed, 
    validationError, 
    goToStep,
    totalSteps,
    allStepsCompleted 
  } = useFormStepper();
  
  // Determine if a step is clickable
  const isStepClickable = (index) => {
    // All steps are now clickable regardless of linear mode
    // Validation will still be enforced when using the "Next" button
    return true;
  };
  
  const handleStepClick = (index) => {
    if (index === activeStep) return; // Don't navigate to current step
    if (isStepClickable(index)) {
      goToStep(index);
    }
  };
  
  return (
    <StepperContainer>
      <Stepper 
        activeStep={activeStep} 
        alternativeLabel={isMobile}
        nonLinear={!linear}
      >
        {steps.map((step, index) => {
          const clickable = isStepClickable(index) && index !== activeStep;
          
          return (
            <Step key={step.label} completed={completed[index]}>
              <StepButton
                onClick={() => handleStepClick(index)}
                disabled={!clickable}
                icon={
                  <CustomStepIcon
                    icon={step.icon ? React.cloneElement(step.icon, { fontSize: 'small' }) : index + 1}
                    error={validationError && activeStep === index}
                    active={activeStep === index}
                    completed={completed[index]}
                    clickable={clickable}
                  />
                }
                optional={step.optional ? <Typography variant="caption">Optional</Typography> : null}
                sx={{
                  cursor: clickable ? 'pointer' : 'default',
                  '& .MuiStepLabel-label': {
                    color: validationError && activeStep === index ? 'error.main' : 'inherit',
                    ...(validationError && activeStep === index && {
                      fontWeight: 500
                    }),
                    ...(clickable && {
                      '&:hover': {
                        color: theme.palette.primary.main,
                        textDecoration: 'underline'
                      }
                    }),
                    // Add visual cue for incomplete future steps
                    ...(index > activeStep && !completed[index] && {
                      opacity: 0.7
                    })
                  },
                  '& .MuiStepButton-root': {
                    cursor: clickable ? 'pointer' : 'default'
                  }
                }}
              >
                {step.label}
              </StepButton>
            </Step>
          );
        })}
        {/* Removed completion step as requested */}
      </Stepper>
    </StepperContainer>
  );
};

/**
 * The inner component that uses the FormStepper context
 */
const FormStepperContent = ({
  showCompletionStep,
  completionContent,
  cancelLink,
  backButtonText,
  nextButtonText,
  submitButtonText,
  submitButtonIcon,
  magical,
  linear,
}) => {
  const { 
    activeStep, 
    totalSteps,
    allStepsCompleted,
    resetStepper,
    getStepComponent
  } = useFormStepper();
  
  // For completion step
  if (activeStep === totalSteps && showCompletionStep) {
    return (
      <CompletionStepContent 
        completionContent={completionContent} 
        onReset={resetStepper} 
      />
    );
  }
  
  return (
    <>
      {/* Progress indicator */}
      <StepperProgressBar />
      
      {/* Current step content */}
      <StepContentContainer>
        {getStepComponent(activeStep)}
      </StepContentContainer>
      
      {/* Navigation buttons */}
      <StepNavigation 
        cancelLink={cancelLink}
        backButtonText={backButtonText}
        nextButtonText={nextButtonText}
        submitButtonText={submitButtonText}
        submitButtonIcon={submitButtonIcon}
        magical={magical}
      />
    </>
  );
};

/**
 * FormStepperEnhanced - Standardized multi-step form component with context
 * 
 * This enhanced version uses context for state management and provides
 * a more consistent API for form steps across different form implementations.
 * 
 * @param {Object} props - Component properties
 * @param {Array} props.steps - Array of step objects with label, icon, and component properties
 * @param {number} props.activeStep - Current active step (for controlled mode)
 * @param {Function} props.onStepChange - Callback when step changes (for controlled mode)
 * @param {Function} props.onSubmit - Callback when form is submitted
 * @param {Function} props.onCancel - Callback when form is cancelled
 * @param {Function} props.validateStep - Function to validate current step before proceeding
 * @param {boolean} props.linear - Whether steps must be completed in order
 * @param {boolean} props.showCompletionStep - Whether to show a completion step
 * @param {React.ReactNode} props.completionContent - Content to show on completion step
 * @param {string} props.nextButtonText - Text for the next button
 * @param {string} props.backButtonText - Text for the back button
 * @param {string} props.submitButtonText - Text for the submit button
 * @param {string} props.cancelLink - Link to navigate to when cancel button is clicked
 * @param {boolean} props.submitting - Whether the form is currently submitting
 * @param {boolean} props.magical - Whether to apply magical styling
 * @param {React.ReactNode} props.submitButtonIcon - Icon for the submit button
 * @param {Object} props.formik - Formik instance for form handling (optional)
 * @param {Object} props.formState - Form state (if not using formik)
 * @param {Function} props.setFormState - Function to update form state (if not using formik)
 * @param {Object} props.sx - Additional styles for the container
 */
const FormStepperEnhanced = ({
  steps = [],
  activeStep: controlledActiveStep,
  onStepChange,
  onNext,
  onBack,
  onSubmit,
  onCancel,
  validateStep,
  linear = true,
  showCompletionStep = false,
  completionContent,
  nextButtonText = "Next",
  backButtonText = "Back",
  submitButtonText = "Submit",
  cancelLink,
  submitting = false,
  magical = false,
  submitButtonIcon,
  formik,
  formState,
  setFormState,
  errors = {},
  touchAllFields = false,
  sx = {},
  ...props
}) => {
  // Enhanced validation error handling for all steps, with special focus on Visual Identity
  React.useEffect(() => {
    // Special handling for the Visual Identity step (step 1)
    if (controlledActiveStep === 1 && formState?.colors) {
      const hasColors = Array.isArray(formState.colors) && formState.colors.length > 0;
      const hasPrimaryColor = hasColors && formState.colors.some(color => color.isPrimary === true);
      
      console.log('FormStepperEnhanced: Checking color validation state:', { 
        hasColors, 
        hasPrimaryColor, 
        colors: formState.colors,
        errors: errors ? Object.keys(errors) : 'no errors'
      });
      
      // Force clear errors if validation conditions are met
      if (hasColors && hasPrimaryColor) {
        console.log('FormStepperEnhanced: Color validation conditions met, clearing validation errors');
        
        // Clear the error state in the DOM
        window.setTimeout(() => {
          // Clear validation error message at stepper level
          const formStepperErrors = document.querySelectorAll('.form-stepper-errors, .error-message');
          formStepperErrors.forEach(el => {
            if (el.textContent?.includes('color') || el.textContent?.includes('Color') || 
                el.textContent?.includes('errors in this step')) {
              console.log('Hiding error element:', el.textContent);
              el.style.display = 'none';
            }
          });
          
          // Clear MUI error states
          const errorFields = document.querySelectorAll('.Mui-error');
          errorFields.forEach(field => {
            if (field.closest('[data-step="1"]')) {
              field.classList.remove('Mui-error');
              const helperText = field.parentElement?.querySelector('.MuiFormHelperText-root');
              if (helperText) {
                helperText.textContent = '';
                helperText.classList.remove('Mui-error');
              }
            }
          });
          
          // Force update FormStepper validation state
          const nextButton = document.querySelector('button[type="button"]:not([disabled])');
          if (nextButton?.textContent?.includes('Next')) {
            console.log('Enabling next button for visual identity step');
            nextButton.disabled = false;
          }
        }, 50);
        
        // Clear error state in the errors object
        if (errors) {
          delete errors['colors.primary'];
          delete errors['colors.exist'];
          delete errors['colors'];
          
          // Also clean general error state keys
          delete errors['validationFailed'];
          delete errors['step1'];
        }
      }
    }
  }, [controlledActiveStep, formState, errors]);

  // Handle controlled vs uncontrolled activeStep
  const handleStepChange = React.useCallback((step) => {
    // Pass to parent if provided
    if (onStepChange) {
      onStepChange(step);
    }
  }, [onStepChange]);
  
  return (
    <Container maxWidth="lg" disableGutters sx={sx} {...props}>
      <GlobalStyles />
      <FormStepperProvider
        steps={steps}
        initialStep={controlledActiveStep || 0}
        linear={linear}
        onSubmit={onSubmit}
        onCancel={onCancel}
        onStepChange={onStepChange}
        validateStep={validateStep}
        formik={formik}
        formState={formState}
        setFormState={setFormState}
        showCompletionStep={showCompletionStep}
        errors={errors}
        touchAllFields={touchAllFields}
      >
        <StyledPaper magical={magical}>
          {/* Stepper visualization */}
          <StepperVisualization linear={linear} />
          
          {/* Step content and navigation */}
          <FormStepperContent
            showCompletionStep={showCompletionStep}
            completionContent={completionContent}
            cancelLink={cancelLink}
            backButtonText={backButtonText}
            nextButtonText={nextButtonText}
            submitButtonText={submitButtonText}
            submitButtonIcon={submitButtonIcon}
            magical={magical}
            linear={linear}
          />
        </StyledPaper>
      </FormStepperProvider>
    </Container>
  );
};

export { FormStep, useFormStepper, FormStepperProvider };
export default FormStepperEnhanced;